<template>
    <Card class="chart">
        <LLoading
            :active="isDatasetReady"
            :is-full-page="false"
            :is-label-exist="false">
            <v-chart
                ref="chart"
                :key="JSON.stringify(datasets)"
                :option="option"
                autoresize
                class="chart"
                @mouseover="assignLineSelection"
                @mouseout="assignLineSelection(null)">
            </v-chart>
        </LLoading>
    </Card>
</template>

<script>
  import { mapActions, mapState } from "vuex";
  import { GET_EFFICIENCY_GRAPH } from "@core/store/action-constants";
  import { moment } from "@/filters";
  import { efficiencyTooltipBuilder } from "./efficiencyTooltipFormatter";
  import LLoading from "@/components/Common/LLoading";
  import { use } from "echarts/core";
  import { BarChart, LineChart } from "echarts/charts";
  import { defaultBarStyle, defaultLineStyle, defaultOptions } from "../../../Charts/echart.config";
  import { THEME_KEY } from "vue-echarts";
  import _merge from "lodash/merge";
  import _cloneDeep from "lodash/cloneDeep";
  import Card from "@/components/Common/Card";
  import _debounce from "lodash/debounce";
  import { EchartMixin } from "@/components/Charts/echart.mixin";

  use([
    BarChart,
    LineChart
  ]);

  export default {
    name: "EfficiencyChart",
    extends: EchartMixin,
    components: { Card, LLoading },
    provide: {
      [THEME_KEY]: "light"
    },
    computed: {
      ...mapState("admin/efficiency", {
        graph: (state) => state.graph,
        total: (state) => state.total,
        chartFilters: (state) => state.chartFilters
      }),

      managers () {
        return this.total?.items
          ?.map(({ admin }) => ({
            id: admin?.intId,
            name: admin?.name,
            login: admin?.login
          })) ?? [];
      },

      xLabels () {
        return this.graph?.map(({ date }) => moment(date, "DD-MM-YYYY")) ?? [];
      },

      isDatasetReady () {
        return this.$wait(`admin/efficiency/${ GET_EFFICIENCY_GRAPH }`);
      },

      datasets () {
        if (!this.graph || !this.managers) {
          return {};
        }

        const lineDataset = [];
        const barDataset = [];

        this.managers.forEach((manager) => {
          const lineData = [];
          const barData = [];

          this.graph.forEach((graphItem) => {
            const founded = graphItem?.managers?.find((item) => item.manager.intId === manager.id) ?? {};
            lineData.push(founded[this.chartFilters.line] ?? 0);
            barData.push(founded[this.chartFilters.bar] ?? 0);
          });

          const defaultData = {
            name: manager.login,
            label: manager.name,
            id: manager.id
          };
          lineDataset.push({
            ...defaultData,
            data: lineData
          });
          barDataset.push({
            ...defaultData,
            data: barData
          });
        });
        return { lineDataset, barDataset };
      },

      lineSeries () {
        return this.$route.query.line ? this.datasets?.lineDataset?.map(({ name, data }, index, array) => {
          const degrees = 360 / array.length * index;
          return {
            ...defaultLineStyle,
            name,
            data,
            emphasis: {
              focus: "series"
            },
            yAxisIndex: 0,
            color: `hsla(${ degrees }deg, 70%, 65%, 1)`
          };
        }) : [];
      },

      barSeries () {
        return this.$route.query.bar ? this.datasets?.barDataset?.map(({ name, data }, index, array) => {
          const degrees = 360 / array.length * index;
          return {
            ...defaultBarStyle,
            name,
            data,
            emphasis: {
              focus: "series"
            },
            yAxisIndex: 1,
            color: `hsla(${ degrees }deg, 70%, 80%, 1)`
          };
        }) : [];
      },

      tooltip () {
        const labels = {};
        const formatters = {};

        this.managers.forEach(({ login, name }) => {
          labels[login] = name;
          formatters[login] = (value) => `${ value ?? 0 }`;
        });

        const filters = Object.values(this.chartFilters).map((filter) => {
          const name = filter ? this.$t(`admin.efficiency.chart.chartFilters.values.${ filter }`) : null;
          return { name, notice: filter === "cashFlow" ? "$" : "" };
        });

        return {
          labels,
          formatters,
          filters
        };
      },

      option () {
        const { line, bar } = this.$route.query;
        return _merge(
          _cloneDeep(defaultOptions),
          {
            legend: {
              data:  this.managers?.map(({ login }) => login) ?? [],
              formatter: (legendName) => this.managers?.find(({ login }) => login === legendName)?.name ?? [],
              left: "center"
            },

            xAxis: {
              type: "category",
              boundaryGap: true,
              data: line || bar ? this.xLabels : null,
              axisTick: {
                alignWithLabel: true
              }
            },

            yAxis: Object.values(this.chartFilters)
              .map((filter, i) => filter ? this.getYAxis(filter, i)
                : {
                  name: "",
                  type: "value",
                  min: 0,
                  max: 100,
                  axisLabel: {
                    show: true,
                    interval: "auto",
                    formatter: "{value} %"
                  }
                }),

            // dataZoom: [
            //   {
            //     type: "slider",
            //     xAxisIndex: [0]
            //   },
            //   {
            //     type: "slider",
            //     yAxisIndex: [0, 1]
            //   }
            // ],

            toolbox: this.toolbox,

            tooltip: {
              // TODO: заменить на richText когда PR будет принят
              // https://github.com/apache/echarts/pull/14994
              renderMode: "html",
              formatter: this.tooltipFormatter(),
              axisPointer: { type: "cross" },
              trigger: "axis"
            },

            series: [
              ...this.lineSeries,
              ...this.barSeries
            ]
          }
        );
      },

      debounceUpdateOptions () {
        return _debounce((event) => {
          this.$refs.chart.setOption({
            tooltip: {
              formatter: this.tooltipFormatter(event ?? {})
            }
          });
        }, 20, { trailing: true });
      }
    },
    methods: {
      ...mapActions("admin/efficiency", [
        GET_EFFICIENCY_GRAPH
      ]),

      tooltipFormatter (seriesName, pinSeriesName) {
        // TODO: Как добавиться график перейти в билдер и дописать логику
        return (params) => efficiencyTooltipBuilder(params, this.tooltip, seriesName, pinSeriesName);
      },

      getYAxis (chosen, index) {
        return {
          name: this.$t(`admin.efficiency.chart.chartFilters.values.${ chosen }`),
          nameTextStyle: {
            align: index ? "right" :"left"
          },
          type: "value",
          nameLocation: "end",
          axisLabel: {
            formatter: `{value} ${ chosen === "cashFlow" ? "$" : "" }`
          }
        };
      },

      assignLineSelection (event) {
        this.debounceUpdateOptions(event);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .yLabel {
    color: #41a748;
  }
</style>